import React, { createContext, useContext, useState, useMemo } from 'react'
import Snackbar from '@/v2/components/common/Snackbar'
import { useCallback } from 'react'

export const SnackbarContext = createContext([])

const snackInitialState = {
  open: false,
  message: '',
  status: 'success',
}

// Callers sometimes hand us an API error payload rather than a string
// (e.g. { dob: "can't be blank" }). Rendering that object as a React child
// throws and takes down the surrounding screen, so flatten it to text.
const toMessageText = message => {
  if (message == null) return ''
  if (typeof message === 'string') return message
  if (Array.isArray(message)) return message.map(toMessageText).join(', ')
  if (typeof message === 'object') {
    return Object.entries(message)
      .map(([field, value]) => `${field}: ${toMessageText(value)}`)
      .join(', ')
  }
  return String(message)
}

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState(snackInitialState)

  const handleOpenSnackbar = useCallback((message = '', status = 'success') => {
    setSnackbar({ open: true, message: toMessageText(message), status })
  }, [])

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(snackInitialState)
  }, [])

  const snackbarInfo = useMemo(
    () => ({
      snackbar,
      onOpenSnackbar: handleOpenSnackbar,
      onCloseSnackbar: handleCloseSnackbar,
    }),
    [snackbar, handleOpenSnackbar, handleCloseSnackbar]
  )
  return (
    <SnackbarContext.Provider value={snackbarInfo}>
      {children}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        status={snackbar.status}
        onClose={() => setSnackbar(snackInitialState)}
      />
    </SnackbarContext.Provider>
  )
}

export const useSnackbarContext = () => {
  const { snackbar, onOpenSnackbar, onCloseSnackbar } = useContext(
    SnackbarContext
  )
  return { snackbar, onOpenSnackbar, onCloseSnackbar }
}
